import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, TextField, Button, Divider, IconButton, Snackbar, Alert, CircularProgress } from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getDeliveryOrderDetailsPublic } from "../../services/Delivery";

const TrackSearch = () => {
  const [searchParams] = useSearchParams();
  const [packageId, setPackageId] = useState("");
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [openError, setOpenError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const orderId = searchParams.get("orderId");
    if (!orderId) return;
    navigate(`/track/order/${orderId}`);
  }, [searchParams]);

  const handleSubmit = async () => {
    if (!packageId.trim()) {
      setErrMsg("Please enter a Package ID");
      setOpenError(true);
      return;
    }
    setLoading(true);
    try {
      const res = await getDeliveryOrderDetailsPublic(packageId);
      if (res) {
        navigate(`/track/${packageId}`, { state: { orderData: res } });
      } else {
        setErrMsg("Package not found");
        setOpenError(true);
      }
    } catch {
      setErrMsg("Failed to fetch package details. Please try again.");
      setOpenError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setOpenError(false);
  };

  return (
    <Box sx={{ width: "100%", height: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#EEF2FF" }}>

      {/* AppBar */}
      <Box sx={{ backgroundColor: "#fff", px: 2, py: 1, display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0, position: "sticky", top: 0, zIndex: 100 }}>
        <Box component="img"  src="/sidebar_Logo.png" alt="logo" sx={{ height:60, objectFit: "contain" }} />
      </Box>

      {/* Main content + Footer scrollable area */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 6,
          background: "linear-gradient(135deg, #EEF2FF 0%, #E0ECFF 40%, #F0F7FF 70%, #EDE9FE 100%)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-80px",
            left: "-80px",
            width: "320px",
            height: "320px",
            borderRadius: "50%",
            background: "rgba(0, 94, 255, 0.07)",
            zIndex: 0,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: "-100px",
            right: "-60px",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background: "rgba(138, 43, 226, 0.06)",
            zIndex: 0,
          },
        }}
      >

        <Box sx={{ textAlign: "center", mb: 4, position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
            <LocalShippingOutlinedIcon sx={{ color: "#005EFF", fontSize: 32 }} />
            <Typography variant="h4" fontWeight={800} sx={{ color: "#005EFF" }}>
              Track All Your Orders
            </Typography>
          </Box>
          <Typography fontWeight={600} color="text.secondary" sx={{ fontSize: "15px",mb:2 }}>
            Logistics Parcel Tracking 
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: "13px" }}>
              It's easy to track your order, simply enter your tracking number.
          </Typography>
        </Box>

        <Paper
          sx={{
            p: 4,
            width: "100%",
            maxWidth: 440,
            position: "relative",
            zIndex: 1,
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            boxShadow: "0 4px 24px rgba(0, 94, 255, 0.10)",
            border: "1px solid rgba(0,94,255,0.08)",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" fontWeight={700}>Track Order</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "13px" }}>
              Enter your package ID below to get live status
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
              Enter Package ID
            </Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="28794022"
              value={packageId}
              onChange={(e) => setPackageId(e.target.value)}
            />
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ textTransform: "none", borderRadius: "8px", py: 1, fontSize: "15px", fontWeight: 600, bgcolor: "#005EFF", "&:hover": { bgcolor: "#0047CC" } }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Track Order"}
          </Button>
        </Paper>
      </Box>

      <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openError} autoHideDuration={5000} onClose={handleSnackbarClose}>
        <Alert severity="error" variant="filled" sx={{ width: "100%" }}>{errMsg}</Alert>
      </Snackbar>
      {/* Footer */}
      <Box sx={{ backgroundColor: "#005EFF", px: 4, py: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 3, mb: 2 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Box component="img" src="/sidebar_Logo.png" alt="logo" sx={{ height:60, objectFit: "contain" }} />
            </Box>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "17px", maxWidth: 350, lineHeight: 1.8 }}>
              4th Floor, Prowess Enterprise (India) Private Limited,<br />
              MRC Building, Opposite to Governor Bungalow,<br />
              Beside Naval House, Siripuram,<br />
              Visakhapatnam, Andhra Pradesh – 530003, India
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
              <IconButton sx={{ color: "rgba(255,255,255,0.8)", p: 0.5, "&:hover": { color: "#fff" } }}>
                <FacebookIcon />
              </IconButton>
              <IconButton sx={{ color: "rgba(255,255,255,0.8)", p: 0.5, "&:hover": { color: "#fff" } }}>
                <InstagramIcon />
              </IconButton>
              <IconButton sx={{ color: "rgba(255,255,255,0.8)", p: 0.5, "&:hover": { color: "#fff" } }}>
                <LinkedInIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 20}}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "18px", mb: 0.5 }}>Company</Typography>
              {["About Us", "Careers", "Blog", "Contact"].map((item) => (
                <Typography key={item} sx={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", cursor: "pointer", "&:hover": { color: "#fff" } }}>{item}</Typography>
              ))}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "18px", mb: 0.5 }}>Services</Typography>
              {["Track Order", "Parcel Delivery", "Warehouse", "Returns"].map((item) => (
                <Typography key={item} sx={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", cursor: "pointer", "&:hover": { color: "#fff" } }}>{item}</Typography>
              ))}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "18px", mb: 0.5 }}>Legal</Typography>
              {["Terms & Conditions", "Privacy Policy", "Cookie Policy", "Disclaimer"].map((item) => (
                <Typography key={item} sx={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", cursor: "pointer", "&:hover": { color: "#fff" } }}>{item}</Typography>
              ))}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 2 }} />

        <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "17px", textAlign: "center" }}>
          © {new Date().getFullYear()} Prowess Logistics Pvt. Ltd. All rights reserved.
        </Typography>
      </Box>

      </Box>
    </Box>
  );
};

export default TrackSearch;
